import { UseEntityIssuesResult } from 'hooks/useEntityIssues';
import InconsistenciesCard, { IssueObjectType } from 'app/extreport/concierge/InconsistenciesCard';
import ClarificationsCard from 'app/content-workflow/element-editor/ClarificationsCard';
import React from 'react';
import { Alert } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import useEverTrue from 'hooks/useEverTrue';
import toast from 'app/toast';

// The issues hook rolls its optimistic patch back and rethrows; the card only wires the buttons,
// so a refused action (e.g. a root with no semantic-model identity) would otherwise fail silently.
function reportFailure<A extends unknown[]>(
    action: (...args: A) => Promise<unknown> | void,
    fallback: string,
): (...args: A) => Promise<void> {
    return async (...args: A) => {
        try {
            await action(...args);
        } catch (error: any) {
            toast('error', error?.response?.data?.data?.message ?? fallback);
        }
    };
}

export interface InconsistenciesWrapperProps {
    issues: UseEntityIssuesResult;
    onOpenConcierge: (questionId?: string) => void;
    showInconsistencies?: boolean;
    // Render the card even if the report never had inconsistencies (a stage that shows the gate)
    keepInconsistenciesCard?: boolean;
    showClarifications?: boolean;
    inconsistenciesExpandToken?: number;
    clarificationsExpandToken?: number;
    objectType?: IssueObjectType;
    // Publishing Board (§3.4): answering a clarification needs the same authority as resolving an inconsistency
    clarificationsNeedKgManager?: boolean;
}

export default function InconsistenciesWrapper({
    issues,
    onOpenConcierge,
    showInconsistencies = true,
    keepInconsistenciesCard = false,
    showClarifications = true,
    inconsistenciesExpandToken,
    clarificationsExpandToken,
    objectType = 'report',
    clarificationsNeedKgManager = false,
}: InconsistenciesWrapperProps) {
    const { t } = useBundleTranslation('app/extreport/concierge');
    const { context, skipQuestion, skipAllQuestions, undoQuestion } = issues;

    // Keep the inconsistencies card rendered (collapsed to `Resolved`) after a refetch clears its
    // rows in-session — resolution happens in the Measure Conflicts editor, not here (spec §4.3/§4.4)
    const everHadInconsistencies = useEverTrue((context?.inconsistencies.length ?? 0) > 0);
    const renderInconsistencies = showInconsistencies && (keepInconsistenciesCard || everHadInconsistencies);

    if (!context) return null;

    const { inconsistencies, clarifyingQuestions } = context;
    // A hidden gate's items never count against "all resolved"
    const inconsistenciesClear = !showInconsistencies || inconsistencies.length === 0;
    const questionsClear = !showClarifications || clarifyingQuestions.every((q) => q.status !== 'Unanswered');
    const hasAnyItems = renderInconsistencies || (showClarifications && clarifyingQuestions.length > 0);
    const allClear = hasAnyItems && inconsistenciesClear && questionsClear;

    return (
        <>
            {allClear && (
                <Alert icon={<IconMi icon={'check'} />} severity={'success'} variant={'standard'}>
                    {t('all_issues_resolved')}
                </Alert>
            )}
            {renderInconsistencies && (
                <InconsistenciesCard
                    inconsistencies={inconsistencies}
                    compact
                    objectType={objectType}
                    canResolve={context.isKgManager === true}
                    forceExpandToken={inconsistenciesExpandToken}
                />
            )}
            {showClarifications && clarifyingQuestions.length > 0 && (
                <ClarificationsCard
                    questions={clarifyingQuestions}
                    onSkip={reportFailure(skipQuestion, t('question_action_failed'))}
                    onUndo={reportFailure(undoQuestion, t('question_action_failed'))}
                    onSkipAll={reportFailure(skipAllQuestions, t('question_action_failed'))}
                    onOpenConcierge={onOpenConcierge}
                    objectType={objectType}
                    readOnly={clarificationsNeedKgManager && context.isKgManager !== true}
                    forceExpandToken={clarificationsExpandToken}
                />
            )}
        </>
    );
}
